import React, { useMemo } from "react";

import { ColumnsType } from "antd/es/table/interface";
import { IntlShape } from "react-intl";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import {
  EntitiesSupportingHistory,
  EntityTypeSupportingHistory,
} from "@mapmycustomers/shared/types/entity";
import {
  EntityHistoryRowWithRecord,
  EntityHistorySourceUpdated,
} from "@mapmycustomers/shared/types/entity/EntityHistoryRow";
import User from "@mapmycustomers/shared/types/User";
import { AvatarWithName } from "@mapmycustomers/ui";

import UserPreviewCard from "@app/component/UserPreviewCard";
import { showEntityView } from "@app/store/entityView/actions";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { formatDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";
import { getRecordSourceTypeDisplayName } from "@app/util/ui";

import FieldColumn from "../component/FieldColumn";
import ValueColumn from "../component/ValueColumn";

const useColumns = (
  intl: IntlShape,
  entityType: EntityTypeSupportingHistory,
  entity: EntitiesSupportingHistory,
  fieldName: string,
  sortOrder: SortOrder,
  showEntityViewAction: typeof showEntityView
): ColumnsType<EntityHistoryRowWithRecord> =>
  useMemo(() => {
    const fieldModel = getFieldModelByEntityType(entityType);
    return [
      {
        dataIndex: "fieldName",
        key: "fieldName",
        render: (_, row: EntityHistoryRowWithRecord) => (
          <FieldColumn fieldModel={fieldModel} historyRow={row} />
        ),
        title: intl.formatMessage({
          id: "@component.entityHistory.table.column.field",
          defaultMessage: "Field",
          description: "Entity history table column - field",
        }),
      },
      {
        dataIndex: "new",
        key: "new",
        render: (_, row: EntityHistoryRowWithRecord) => (
          <ValueColumn
            entity={entity}
            entityType={entityType}
            fieldModel={fieldModel}
            historyRow={row}
            showEntityView={showEntityViewAction}
            value={row.new}
          />
        ),
        title: intl.formatMessage({
          id: "@component.entityHistory.table.column.newValue",
          defaultMessage: "New Value(s)",
          description: "Entity history table column - New Value(s)",
        }),
      },
      {
        dataIndex: "old",
        key: "old",
        render: (_, row: EntityHistoryRowWithRecord) => (
          <ValueColumn
            entity={entity}
            entityType={entityType}
            fieldModel={fieldModel}
            hideAdditionsAndRemovals
            historyRow={row}
            showEntityView={showEntityViewAction}
            value={row.old}
          />
        ),
        title: intl.formatMessage({
          id: "@component.entityHistory.table.column.oldValue",
          defaultMessage: "Old Value(s)",
          description: "Entity history table column - Old Value(s)",
        }),
      },
      {
        dataIndex: "updatedAt",
        key: "updatedAt",
        render: (updatedAt: string) => formatDate(parseApiDate(updatedAt), "PPpp"),
        sorter: true,
        sortOrder:
          fieldName === "updatedAt"
            ? sortOrder === SortOrder.ASC
              ? "ascend"
              : "descend"
            : undefined,
        title: intl.formatMessage({
          id: "@component.entityHistory.table.column.date",
          defaultMessage: "Date",
          description: "Entity history table column - Date",
        }),
      },
      {
        dataIndex: "sourceUpdated",
        key: "sourceUpdated.clientType",
        render: (sourceUpdated: EntityHistorySourceUpdated) =>
          getRecordSourceTypeDisplayName(intl, sourceUpdated.clientType),
        title: intl.formatMessage({
          id: "@component.entityHistory.table.column.source",
          defaultMessage: "Source",
          description: "Entity history table column - Source",
        }),
      },
      {
        dataIndex: "updatedBy",
        key: "updatedBy.id",
        render: (user: User) =>
          user ? (
            <UserPreviewCard userId={user.id}>
              <div>
                <AvatarWithName actionOnHover user={user} />
              </div>
            </UserPreviewCard>
          ) : (
            ""
          ),
        sorter: true,
        sortOrder:
          fieldName === "updatedBy.id"
            ? sortOrder === SortOrder.ASC
              ? "ascend"
              : "descend"
            : undefined,
        title: intl.formatMessage({
          id: "@component.entityHistory.table.column.user",
          defaultMessage: "User",
          description: "Entity history table column - User",
        }),
      },
    ];
  }, [intl, entityType, entity, showEntityViewAction, fieldName, sortOrder]);

export default useColumns;
